/**
 * PARTAGE — la moitié du concept de Villopia (§9).
 *
 * « Chaque visite unique déclenche un boost » est la phrase qui définit le jeu. Elle
 * suppose un geste avant tout le reste : que le joueur envoie son lien. Ce fichier est ce
 * geste, et rien d'autre — la comptabilisation de la visite et le boost viennent après,
 * côté serveur.
 *
 * ── Pourquoi la logique est séparée de l'écran ────────────────────────────────
 *
 * Composer l'URL et le message est **pur** : deux fonctions, testables sans navigateur ni
 * DOM. Le partage lui-même ne l'est pas — il dépend de trois API dont aucune n'est
 * garantie (`navigator.share`, `navigator.clipboard`, la sélection de texte) — donc il
 * est isolé dans une seule fonction qui dit LAQUELLE a servi. Un bouton qui ne sait pas
 * s'il a réussi ne peut pas donner de retour honnête au joueur, et un partage silencieux
 * est indistinguable d'un partage cassé.
 *
 * `src/viral` n'est pas soumis à la pureté de `src/core` (§4) mais suit la même
 * discipline : aucun texte composé ici, les libellés viennent de `src/i18n`.
 */

/** Base publique des villes. Une seule source, pour que le lien partagé soit stable. */
export const CITY_PATH = '/ville';

/**
 * Lien public d'une ville.
 *
 * `origin` est passé par l'appelant plutôt que lu dans `location` : c'est ce qui rend la
 * fonction testable, et c'est aussi ce qui permettra de forcer le domaine canonique le
 * jour où `villopia.app` servira le jeu — un lien partagé depuis `villopia.pages.dev`
 * resterait valide, mais il ne fait pas la même impression.
 */
export function cityUrl(origin: string, slug: string): string {
  // Pas de slash doublé si l'origine en porte déjà un : un lien à double slash marche,
  // mais il se voit, et un lien partagé est vu par quelqu'un d'autre que son auteur.
  return `${origin.replace(/\/+$/, '')}${CITY_PATH}/${encodeURIComponent(slug)}`;
}

/** Ce qu'on tente pour partager, dans l'ordre. Le retour dit ce qui a réellement servi. */
export type ShareMethod = 'native' | 'clipboard' | 'manual';

export interface ShareResult {
  readonly method: ShareMethod;
  /** `false` quand le joueur a annulé la feuille de partage : ce n'est pas un échec. */
  readonly shared: boolean;
}

export interface ShareRequest {
  readonly title: string;
  readonly text: string;
  readonly url: string;
}

/**
 * Partage le lien par le meilleur chemin disponible.
 *
 * 1. **`navigator.share`** — la feuille native. C'est le seul chemin qui ouvre WhatsApp,
 *    les SMS ou Instagram en un geste, donc le seul qui rende le partage réellement
 *    viral sur téléphone. Absent sur la plupart des navigateurs de bureau.
 * 2. **Le presse-papiers** — le repli de bureau. Le joueur colle où il veut.
 * 3. **`manual`** — ni l'un ni l'autre (contexte non sécurisé, permission refusée).
 *    L'appelant doit alors montrer le lien et laisser le joueur le sélectionner. On ne
 *    prétend jamais avoir partagé.
 *
 * Une annulation de la feuille native rend `shared: false` **sans basculer sur le
 * presse-papiers** : le joueur a dit non, lui copier le lien d'office serait le
 * contredire.
 */
export async function shareCity(
  request: ShareRequest,
  navigatorLike: Pick<Navigator, 'share' | 'clipboard'> = navigator,
): Promise<ShareResult> {
  if (typeof navigatorLike.share === 'function') {
    try {
      await navigatorLike.share(request);
      return { method: 'native', shared: true };
    } catch (error) {
      // `AbortError` = le joueur a fermé la feuille. Tout autre échec signifie que le
      // chemin natif n'a pas marché, et on tente alors le presse-papiers.
      if (error instanceof Error && error.name === 'AbortError') {
        return { method: 'native', shared: false };
      }
    }
  }

  try {
    await navigatorLike.clipboard.writeText(request.url);
    return { method: 'clipboard', shared: true };
  } catch {
    return { method: 'manual', shared: false };
  }
}
